import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Chip,
  Alert,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShieldIcon from '@mui/icons-material/Shield';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../api';

interface User {
  id: number;
  username: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

const emptyForm = { username: '', password: '', role: 'operator' as 'admin' | 'operator' };

export default function UsersPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = useMemo(() => language === 'ru' ? ru : enUS, [language]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { user: currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (err: any) {
      enqueueSnackbar(t('users.errorLoading'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!editingUser && !form.username.trim()) errors.username = t('users.loginRequired');
    if (!editingUser && form.password.length < 4) errors.password = t('users.passwordMinLength');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (editingUser) {
        const payload: any = { role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingUser.id}`, payload);
        enqueueSnackbar(t('users.snackbarUpdated'), { variant: 'success' });
      } else {
        await api.post('/users', form);
        enqueueSnackbar(t('users.snackbarCreated'), { variant: 'success' });
      }
      setDialogOpen(false);
      setEditingUser(null);
      setForm(emptyForm);
      fetchUsers();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (u: User) => {
    setEditingUser(u);
    setForm({ username: u.username, password: '', role: u.role as 'admin' | 'operator' });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/users/${deleteConfirm.id}`);
      enqueueSnackbar(t('users.snackbarDeleted'), { variant: 'success' });
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #f472b6 20%, #8b8bff 80%)'
                : 'linear-gradient(135deg, #ec4899 20%, #5b5bf6 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(244,114,182,0.55)'
                : '0 8px 20px -8px rgba(236,72,153,0.4)',
            }}
          >
            <ShieldIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('users.intro')} · {t('users.total')}: <strong style={{ color: 'var(--text)' }}>{users.length}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingUser(null); setForm(emptyForm); setFormErrors({}); setDialogOpen(true); }}
        >
          {t('common.add')}
        </Button>
      </Box>

      <Paper sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('users.tableLogin')}</TableCell>
                <TableCell>{t('users.tableRole')}</TableCell>
                <TableCell>{t('users.tableCreatedAt')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                    {t('users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => {
                  const isSelf = currentUser?.id === u.id;
                  return (
                    <TableRow key={u.id} sx={isSelf ? {
                      background: isDark
                        ? 'linear-gradient(90deg, rgba(139,139,255,0.08), transparent 60%)'
                        : 'linear-gradient(90deg, rgba(91,91,246,0.06), transparent 60%)',
                    } : {}}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {u.username}
                          </Typography>
                          {isSelf && (
                            <Chip
                              label={t('users.you')}
                              size="small"
                              sx={{
                                background: isDark
                                  ? 'linear-gradient(135deg, rgba(139,139,255,.2), rgba(94,231,241,.18))'
                                  : 'linear-gradient(135deg, rgba(91,91,246,.2), rgba(34,195,209,.18))',
                                color: 'var(--primary)',
                                fontWeight: 600,
                                border: 'none',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.role === 'admin' ? t('users.roleAdmin') : t('users.roleOperator')}
                          size="small"
                          sx={{
                            background:
                              u.role === 'admin'
                                ? isDark ? 'rgba(139,139,255,0.15)' : 'rgba(91,91,246,0.12)'
                                : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
                            color: 'inherit',
                            fontWeight: 600,
                            border: 'none',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                        {format(parseISO(u.createdAt), 'dd.MM.yyyy HH:mm', { locale })}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(u)} aria-label={t('common.edit')}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteConfirm(u)}
                          disabled={isSelf}
                          aria-label={t('common.delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? t('users.editUser') : t('users.addUser')}</DialogTitle>
        <DialogContent>
          {editingUser ? (
            <Alert severity="info" sx={{ mb: 2, mt: 1, borderRadius: 1.5 }}>
              <Trans i18nKey="users.loginReadonly" values={{ login: editingUser.username }} components={{ strong: <strong /> }} />
            </Alert>
          ) : (
            <TextField
              fullWidth
              label={t('users.formLogin')}
              required
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              error={!!formErrors.username}
              helperText={formErrors.username}
              sx={{ mt: 1 }}
            />
          )}
          <TextField
            fullWidth
            label={editingUser ? t('users.formNewPassword') : t('users.formPassword')}
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            error={!!formErrors.password}
            helperText={formErrors.password}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('users.formRole')}</InputLabel>
            <Select
              value={form.role}
              label={t('users.formRole')}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'operator' })}
            >
              <MenuItem value="admin">{t('users.roleAdmin')}</MenuItem>
              <MenuItem value="operator">{t('users.roleOperator')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('users.deleteUser')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-muted)' }}>
            {t('users.deleteConfirm', { username: deleteConfirm?.username })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}